import { isPlatformBrowser } from "@angular/common";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable, PLATFORM_ID } from "@angular/core";
import { EventSpace, EventSpaceRequestBody } from "@models/space";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { LocalStorageService } from "./local-storage.service";

@Injectable({
  providedIn: "root",
})
export class SpaceManagementService {
  private readonly apiUrl = environment.API_BACKEND;

  private readonly http = inject(HttpClient);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly platformId = inject(PLATFORM_ID);

  private getToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return this.localStorageService.getItem("auth-token");
    }
    return null;
  }

  private getHttpHeaders(): HttpHeaders {
    const token = this.getToken();
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
  }

  getSpaces(eventId: number): Observable<EventSpace[]> {
    return this.http.get<EventSpace[]>(
      `${this.apiUrl}/events/${eventId}/spaces`,
      { headers: this.getHttpHeaders() },
    );
  }

  createSpace(
    eventId: number,
    body: EventSpaceRequestBody,
  ): Observable<EventSpace> {
    return this.http.post<EventSpace>(
      `${this.apiUrl}/events/${eventId}/spaces`,
      body,
      { headers: this.getHttpHeaders() },
    );
  }

  updateSpace(
    spaceId: number,
    body: EventSpaceRequestBody,
  ): Observable<EventSpace> {
    return this.http.patch<EventSpace>(
      `${this.apiUrl}/spaces/${spaceId}`,
      body,
      { headers: this.getHttpHeaders() },
    );
  }

  deleteSpace(spaceId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/spaces/${spaceId}`,
      { headers: this.getHttpHeaders() },
    );
  }
}
